import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAppointments, type AppointmentRow } from '@/hooks/data/useAppointments';
import { useClients } from '@/hooks/data/useClients';
import { useAuth } from '@/hooks/useAuth';
import { AppointmentsTable } from './appointments';
import { CreateAppointmentDialog } from '@/components/appointments/CreateAppointmentDialog';

export const Route = createFileRoute('/_app/appointments/acquisition')({
  component: AcquisitionPage,
});

function AcquisitionPage() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const [creating, setCreating] = useState(false);

  const [scope, setScope] = useState<'mine' | 'all'>(() => {
    const saved = typeof localStorage !== 'undefined' ? localStorage.getItem('appointments.scope') : null;
    if (saved === 'mine' || saved === 'all') return saved;
    return profile?.role === 'admin' ? 'all' : 'mine';
  });
  useEffect(() => {
    if (typeof localStorage !== 'undefined') localStorage.setItem('appointments.scope', scope);
  }, [scope]);

  // Not persisted — see the note on /appointments. A stored search term made
  // the list look empty on every later visit.
  const [searchQuery, setSearchQuery] = useState<string>('');
  useEffect(() => {
    if (typeof localStorage !== 'undefined') localStorage.removeItem('appointments.search');
  }, []);

  const { data: items = [], isLoading } = useAppointments({ type: 'aufnahme', limit: 10000 });
  const { data: clients = [] } = useClients({ limit: 10000 });
  const clientMap = useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients]);

  const normalize = (s: string | null | undefined) =>
    (s ?? '').toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
  const filtered = useMemo(() => {
    const q = normalize(searchQuery.trim());
    return items.filter((a) => {
      // Defence-in-depth: backend already filters via ?type=aufnahme but
      // a stale TanStack cache from another route could leak other types
      // until the next refetch. Drop them client-side too.
      if (a.appointment_type !== 'aufnahme') return false;
      if (scope === 'mine' && profile) {
        if (a.agent_id !== profile.id && a.created_by !== profile.user_id) return false;
      }
      if (!q) return true;
      const c = a.client_id ? clientMap.get(a.client_id) : null;
      const cName = c ? `${c.title ?? ''} ${c.first_name ?? ''} ${c.last_name}` : '';
      return [cName, a.phone ?? '', a.agent_full_name ?? '', a.notes ?? ''].some((f) =>
        normalize(f).includes(q),
      );
    });
  }, [items, scope, profile, clientMap, searchQuery]);

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">
            {t('appointments.acquisition.title', 'Aufnahme-Termine')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t(
              'appointments.acquisition.subtitle',
              'Termine zur Auftragsaufnahme bei Verkäufern.',
            )}
          </p>
        </div>
        <Button onClick={() => setCreating(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          {t('appointments.create.cta', 'Neuer Termin')}
        </Button>
      </header>

      <div className="fm-card p-5 space-y-4">
        <div className="relative">
          <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t(
              'appointments.search_placeholder',
              'Suchen — Klient, Telefon, Makler, Notiz …',
            )}
            className="w-full rounded-md border border-line bg-card ps-9 pe-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
          />
        </div>

        <div className="flex gap-1 rounded-md bg-paper-2 p-1 w-fit">
          <button
            type="button"
            onClick={() => setScope('mine')}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
              scope === 'mine' ? 'bg-card text-ink shadow-soft' : 'text-ink-3 hover:text-ink'
            }`}
          >
            {t('appointments.scope.mine', 'Meine')}
          </button>
          <button
            type="button"
            onClick={() => setScope('all')}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
              scope === 'all' ? 'bg-card text-ink shadow-soft' : 'text-ink-3 hover:text-ink'
            }`}
          >
            {t('appointments.scope.all', 'Alle')}
          </button>
        </div>

        {isLoading ? (
          <p className="py-8 text-center text-sm text-ink-3">{t('common.loading', 'Lädt …')}</p>
        ) : filtered.length === 0 ? (
          <p className="py-8 text-center text-sm text-ink-3">
            {t('appointments.empty', 'Keine Termine.')}
          </p>
        ) : (
          <AppointmentsTable items={filtered as AppointmentRow[]} clientMap={clientMap} />
        )}
      </div>

      <CreateAppointmentDialog
        open={creating}
        onOpenChange={setCreating}
        defaultType="aufnahme"
      />
    </section>
  );
}
